import React, { useCallback, useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getVideoSelector } from 'redux/modules/embed/selectors';
import { useTrackAssetContext } from '../../components/TrackAsset';
import { millisToPosition, positionToMillis } from '../../utils';
import {
  createFramingOriginMillisSelector,
  createLayoutTimelineOffsetMillisSelector,
} from './selectors';
import {
  FramingTimelineContextType,
  FramingTimelineProviderProps,
} from './types';

const FramingTimelineContext = React.createContext<
  FramingTimelineContextType | undefined
>(undefined);

export const FramingTimelineProvider: React.FC<FramingTimelineProviderProps> = ({
  children,
}) => {
  const { id: videoId, pxPerSec } = useTrackAssetContext();
  const originMillis = useSelector(createFramingOriginMillisSelector(videoId));
  const video = useSelector(getVideoSelector(videoId));

  const offsetMillis = useSelector(
    createLayoutTimelineOffsetMillisSelector(videoId),
  );

  const convertAssetTimeToFramingTime = useCallback(
    (millis: number) => millis - originMillis,
    [originMillis],
  );

  const convertAssetTimeToFramingPosition = useCallback(
    (millis: number) =>
      millisToPosition(convertAssetTimeToFramingTime(millis), pxPerSec),
    [convertAssetTimeToFramingTime, pxPerSec],
  );

  const convertFramingPositionToAssetTime = useCallback(
    (position: number) => positionToMillis(position, pxPerSec) + originMillis,
    [originMillis, pxPerSec],
  );

  const convertAssetTimeToTimelinePosition = useCallback(
    (millis: number) =>
      millisToPosition(video?.startMillis + (millis - originMillis), pxPerSec),
    [originMillis, pxPerSec, video?.startMillis],
  );

  return (
    <FramingTimelineContext.Provider
      value={useMemo(
        () => ({
          offsetMillis,
          offsetPx: millisToPosition(offsetMillis, pxPerSec),
          originPx: millisToPosition(originMillis, pxPerSec),
          originMillis,
          convertAssetTimeToFramingTime,
          convertAssetTimeToFramingPosition,
          convertFramingPositionToAssetTime,
          convertAssetTimeToTimelinePosition,
        }),
        [
          convertAssetTimeToFramingPosition,
          convertAssetTimeToFramingTime,
          convertAssetTimeToTimelinePosition,
          convertFramingPositionToAssetTime,
          offsetMillis,
          originMillis,
          pxPerSec,
        ],
      )}
    >
      {children}
    </FramingTimelineContext.Provider>
  );
};

export function useFramingTimelineContext() {
  const context = useContext(FramingTimelineContext);

  if (context === undefined) {
    throw new Error(
      'useFramingTimelineContext must be used within FramingTimelineProvider',
    );
  }

  return context;
}
